import React from "react";
import "./About.css";

export default function About() {
  return (
    <div className="about-page">
      <section className="about-hero">
        <div className="about-hero-card">
          <div className="about-badge">About Conscious Neurons</div>

          <h1 className="about-title">
            Building intelligent systems with structure, clarity, and human
            intent.
          </h1>

          <p className="about-lead">
            Conscious Neurons is a software and AI company focused on creating
            systems that feel thoughtful, explainable, and aligned with real
            human use. We approach engineering as more than code delivery. We
            treat it as the design of intelligence, interaction, and long-term
            product behavior.
          </p>
        </div>
      </section>

      <section className="about-section">
        <div className="about-section-head">
          <div className="about-section-tag">Who we are</div>
          <h2>AI-first, product-minded, and built around clarity</h2>
          <p>
            Founded by visionary technologists, Conscious Neurons operates as a
            modern software lab focused on meaningful AI and intelligent product
            systems. Our goal is not just to automate tasks, but to create tools
            that amplify human capability and reduce unnecessary complexity.
          </p>
        </div>

        <div className="about-grid about-grid-2">
          <article className="about-card">
            <h3>Technology with awareness</h3>
            <p>
              We believe artificial intelligence should feel intuitive,
              reliable, and empowering. It should support better thinking,
              better systems, and better decisions instead of becoming a black
              box that people are forced to trust without understanding.
            </p>
          </article>

          <article className="about-card">
            <h3>Design with intent</h3>
            <p>
              Our philosophy blends deep neural computation with conscious
              design principles. That means strong architecture, explainable
              behavior, and interfaces that feel deliberate instead of chaotic.
            </p>
          </article>
        </div>
      </section>

      <section className="about-section">
        <div className="about-section-head">
          <div className="about-section-tag">How we think</div>
          <h2>Where intelligence, systems, and experience meet</h2>
          <p>
            We work at the intersection of data science, automation, software
            architecture, and cognitive design. The aim is to merge analytical
            precision with human-centered thinking so products do not just work
            technically, but make sense experientially.
          </p>
        </div>

        <div className="about-grid about-grid-3">
          <article className="about-card">
            <h3>Neural and AI systems</h3>
            <p>
              We explore architectures that learn, adapt, and operate
              meaningfully inside the environments they are built for.
            </p>
          </article>

          <article className="about-card">
            <h3>Automation with structure</h3>
            <p>
              We design systems that simplify complexity without flattening the
              nuance of real workflows.
            </p>
          </article>

          <article className="about-card">
            <h3>Software with coherence</h3>
            <p>
              Every layer of a product should support the next one, from
              interface to logic to backend behavior.
            </p>
          </article>
        </div>
      </section>

      <section className="about-section">
        <div className="about-feature-card">
          <h2>More than a software company</h2>
          <p>
            At Conscious Neurons, we embrace the idea that AI is not just a
            utility layer. It is a co-evolutionary force that can help
            individuals, teams, and organizations grow when it is built with
            awareness, transparency, and care.
          </p>
          <p>
            We challenge the idea that advanced software must feel cold or
            distant. Our work is guided by the belief that intelligence systems
            should feel integrated with human goals, not detached from them.
          </p>
        </div>
      </section>

      <section className="about-section">
        <div className="about-section-head">
          <div className="about-section-tag">Our team</div>
          <h2>Engineers, researchers, and system architects</h2>
          <p>
            Conscious Neurons brings together people who care about both
            technical excellence and the deeper purpose of the systems they
            build.
          </p>
        </div>

        <div className="about-grid about-grid-2">
          <article className="about-card">
            <h3>Technical depth</h3>
            <p>
              Our team combines experience in machine learning, software
              architecture, automation, and cognitive systems. We care about
              quality at the implementation level and coherence at the system
              level.
            </p>
          </article>

          <article className="about-card">
            <h3>Human-centered direction</h3>
            <p>
              We also believe good systems come from more than raw logic. They
              come from understanding people, attention, trust, and the real
              contexts where software lives.
            </p>
          </article>
        </div>

        <div className="about-quote-card">
          <p>The founding vision was born from a simple question:</p>
          <blockquote>
            What if technology did not just work — what if it felt conscious?
          </blockquote>
          <p>
            That question continues to shape how we think about intelligence,
            design, product systems, and the future of software itself.
          </p>
        </div>
      </section>
    </div>
  );
}
